/*  
<summary>
  Functional Component: Cancelled Reservation
  Developer: Sahil Bharti
  Created Date: 16-4-2025
  Description: This component manages the display of cancelled reservations, pagination, and customer details.
   It allows for viewing reservation details, managing cancellation statuses and also see customer info
</summary>
<returns>JSX for displaying cancelled reservation details, with pagination, date filters, and customer information.</returns>
*/

import { observer } from "mobx-react";
import moment from "moment";
import "rc-tree/assets/index.css";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import userSettingEnum from "../../../constants/enums/user-setting-enum";
import { useStore } from "../../../contexts/store-provider";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import Pagination from "../../../shared-components/Grid/Pagination";
import { formatMessage } from "../../../translations/format-message";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import CustomDatePicker from "../../../shared-components/date-picker/custome-date-picker";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import AddEditCustomerManagement from "../../customer-management/components/add-edit-customer-management";
import { CancellationTypeEnum } from "../../../constants/enums/cancellation-type-enum";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
const CancelledReservation: React.FC = (props: any) => {
  const history = useHistory();
  const { preferencesStore, bookingManagementStore, customerManagementStore } = useStore();
  const [openPanel, setOpenPanel] = useState(false);
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    cancelledReservationList,
    GetCancelledReservationListService,
    getCancelledReservationListState,
    allCancelledReservations,
  } = bookingManagementStore;
  const {addUpdateCustomerState, resetAddUpdateCustomerState} = customerManagementStore;

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const validateDates = (start: any, end: any) => {
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      // Reset time to compare only dates
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (startDate > endDate) {
        return false;
      }
    }
    return true;
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
  };

  useEffect(() => {
    if (startDate && endDate && validateDates(startDate, endDate)) {
      callGetCancelledReservationListService();
    }
    else{
      if(endDate && startDate)
        toast.error(formatMessage("end_date_must_be_same_or_after_start_date"));
    }
  }, [startDate, endDate]);

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  /**
   * Calls API to fetch cancelled reservations using store ID, date range, and pagination.
   */

  const callGetCancelledReservationListService = () => {
    const storeId = getStoreIdFromLocalStorage();

    //valid date strings in "YYYY-MM-DD" format
    const safeStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD");
    const safeEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().endOf("month").format("YYYY-MM-DD");

    GetCancelledReservationListService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      safeStartDate,
      safeEndDate,
      storeId
    );
  };
  
  useEffect(() => {
    if(addUpdateCustomerState.success){
      callGetCancelledReservationListService();
      resetAddUpdateCustomerState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateCustomerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: cancelledReservationList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCancelledReservations]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetCancelledReservationListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetCancelledReservationListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  const openReservation = (rowId: any) => {
    const customerId = rowId.CustomerId;
    setSelectedCustomer(customerId);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetCancelledReservationListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  return (
    <React.Fragment>
      {(getCancelledReservationListState.inProgress) && (
        <FormLoader
          loading={
            getCancelledReservationListState.inProgress
          }
        />
      )}
      {isFormOpen && selectedCustomer && (
        <AddEditCustomerManagement
          id={selectedCustomer}
          modalClosed={closeForm}
          viewOnly={false}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="cancelled_reservation" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="reservation_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="cancelled_reservation" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row justify-content-between align-items-end">
          <div className="col-xxl-4 col-xl-5 col-lg-6 col-md-7 col-sm-8  col-12 mb-3">
            <div className="row">
              <div className="col-6">
                <label className="">
                  <FormattedMessage id="start_date" />
                </label>
                <CustomDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                />
              </div>
              <div className="col-6">
                <label className="">
                  <FormattedMessage id="end_date" />
                </label>
                <CustomDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={`${openPanel ? "panelOpen" : ""}`}>
          {
            validateDates(startDate, endDate) ?
            <>
              {allCancelledReservations?.length > 0 ? (
                allCancelledReservations?.map((reservations: any) => {
                  return (
                    <div className="reservation_list_wrapper px-3 mb-3">
                      <div className="row align-items-center justify-content-between">
                        <div className="col-xl-9 col-lg-8 col-sm-8 col-7 p-3 border-end">
                          <div className="row align-items-center mb-2">
                            <div className="col-8">
                              <h3 className="mb-0">
                                {reservations?.CustomerName || "N/A"}
                              </h3>
                            </div>

                            <div className="col-4 text-end">
                              {reservations?.CancelationType ===
                              CancellationTypeEnum.Web ? (
                                <span className="event-mode bg-primary text-white px-2 py-1 rounded-circle">
                                  <i className="bi bi-globe " title="Web"></i>
                                </span>
                              ) : reservations?.CancelationType ===
                                CancellationTypeEnum.Visit ? (
                                <span className="event-mode bg-primary text-white px-2 py-1 rounded-circle">
                                  <i
                                    className="bi bi-shop-window"
                                    title="Visit"
                                  ></i>
                                </span>
                              ) : reservations?.CancelationType ===
                                CancellationTypeEnum.OnCall ? (
                                <span className="event-mode bg-primary text-white px-2 py-1 rounded-circle">
                                  <i
                                    className="bi bi-telephone"
                                    title="On Call"
                                  ></i>
                                </span>
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                          <div className="row g-0">
                            <div className="col-sm-6 col-12 mb-3 mb-sm-0">
                              {/* Reservation Time */}
                              <div className="d-flex">
                                <i className="bi bi-calendar-event me-1"></i>
                                <div>
                                  <h5 className="m-0">
                                    {formatMessage("reservation_time")}
                                  </h5>
                                  <span className="text-muted">
                                    {moment(reservations.Date).format(
                                      "YYYY-MM-DD "
                                    )}{" "}
                                    {moment(reservations.Time, "HH:mm:ss").format(
                                      "HH:mm"
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-sm-6 col-12 mb-3 mb-sm-0">
                              {/* Cancellation Time */}
                              <div className="d-flex">
                                <i className="bi bi-calendar-event me-1"></i>
                                <div>
                                  <h5 className="m-0">
                                    {formatMessage("cancellation_time")}
                                  </h5>
                                  <span className="text-muted">
                                    {reservations?.CancellationTimeFormatted}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-3 col-lg-4 col-sm-4 col-5 text-end">
                          <button
                            className="btn w-100 btn-primary"
                            onClick={() => openReservation(reservations)}
                          >
                            <svg
                              className="me-2"
                              version="1.1"
                              width="16px"
                              height="16px"
                              id="fi_709612"
                              x="0px"
                              y="0px"
                              viewBox="0 0 511.999 511.999"
                              fill="currentColor"
                            >
                              <g>
                                <g>
                                  <path
                                    d="M508.745,246.041c-4.574-6.257-113.557-153.206-252.748-153.206S7.818,239.784,3.249,246.035
              c-4.332,5.936-4.332,13.987,0,19.923c4.569,6.257,113.557,153.206,252.748,153.206s248.174-146.95,252.748-153.201
              C513.083,260.028,513.083,251.971,508.745,246.041z M255.997,385.406c-102.529,0-191.33-97.533-217.617-129.418
              c26.253-31.913,114.868-129.395,217.617-129.395c102.524,0,191.319,97.516,217.617,129.418
              C447.361,287.923,358.746,385.406,255.997,385.406z"
                                  ></path>
                                </g>
                              </g>
                              <g>
                                <g>
                                  <path
                                    d="M255.997,154.725c-55.842,0-101.275,45.433-101.275,101.275s45.433,101.275,101.275,101.275
              s101.275-45.433,101.275-101.275S311.839,154.725,255.997,154.725z M255.997,323.516c-37.23,0-67.516-30.287-67.516-67.516
              s30.287-67.516,67.516-67.516s67.516,30.287,67.516,67.516S293.227,323.516,255.997,323.516z"
                                  ></path>
                                </g>
                              </g>
                            </svg>
                            <FormattedMessage id="see_customer_info" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="message text-center">
                  <img src={noMessageIcon} alt="" className="no_message" />
                  <p className="noData text-muted text-center h4 mb-0">
                    <FormattedMessage id="no_cancelled_reservations" />
                  </p>
                  <p className="mb-3">
                    {formatMessage("no_cancelled_reservations_message")}
                  </p>
                </div>
              )}
            </> :
            <div className="message text-center">
              <img src={noMessageIcon} alt="" className="no_message" />
              <p className="noData text-muted text-center h4 mb-0">
                <FormattedMessage id="no_cancelled_reservations" />
              </p>
              <p className="mb-3">
                {formatMessage("end_date_must_be_same_or_after_start_date")}
              </p>
            </div>
          }
        </div>
        {(allCancelledReservations?.length > 0 && validateDates(startDate, endDate)) && (
          <div className="d-flex align-items-center justify-content-center p-2 tableFooter">
            <Pagination
              isRefreshPage={pageInfo.isRefreshPage}
              totalPages={pageInfo.totalPages}
              pageLimit={pageLimitInfo.pageLimit}
              onPageChanged={onPageChanged}
              disablePagination={disablePagination}
              setIsRefreshPage={setIsRefreshPage}
            />
            <select
              data-testid="pageSelect"
              className="pageLimit"
              onChange={onPageLimitChanged}
              value={pageLimitInfo.pageLimit}
            >
              {pageLimitOptions.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default observer(CancelledReservation);
